import {
  type BillingPeriod,
  ENTITY_STATUS,
  type EntityStatus,
  type TargetAudience,
} from '@monorepo/shared/schemas/common'
import {
  AlertCircle,
  Archive,
  CheckCircle,
  ChevronRight,
  Copy,
  Edit,
  Loader2,
  Play,
  Timer,
} from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { toast } from '@/shared/lib/toast'
import { useAddonTrial } from '../../hooks/useAddons'
import {
  getBillingPeriodLabel,
  getTargetAudienceColor,
  getTargetAudienceLabel,
} from '../../utils/addonFormatters'

interface AddonPreviewCardProps {
  name: string
  planId: string
  status: EntityStatus
  currency: string
  billing_period?: BillingPeriod
  target_audience?: TargetAudience
  activeMenu: 'pricing' | 'entitlements'
  onEdit: () => void
  onToggleStatus: () => void
  onActiveMenuChange: (menu: 'pricing' | 'entitlements') => void
}

export function AddonPreviewCard({
  name,
  planId,
  status,
  currency,
  billing_period,
  target_audience,
  activeMenu,
  onEdit,
  onToggleStatus,
  onActiveMenuChange,
}: AddonPreviewCardProps) {
  // Use backend state for trial settings
  const { data: trialSettings, isLoading: isTrialLoading } =
    useAddonTrial(planId)

  const handleCopyPlanId = async () => {
    try {
      await navigator.clipboard.writeText(planId)
      toast.success('Addon ID copied to clipboard')
    } catch {
      toast.error('Failed to copy Addon ID')
    }
  }

  return (
    <div className='space-y-4'>
      <div className='border rounded-xl p-8 bg-gradient-to-br from-background to-muted/20 min-h-[350px] flex flex-col'>
        <div className='mb-6'>
          {/* Plan Name - Full Width */}
          <h3 className='font-semibold text-lg mb-3 leading-relaxed'>
            {name || 'Plan Name'}
          </h3>

          {/* Plan ID with Copy Button */}
          <div className='flex items-center gap-2 mb-4'>
            <div className='flex-1 min-w-0'>
              <p className='text-sm text-muted-foreground font-mono truncate'>
                {planId}
              </p>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleCopyPlanId}
              className='h-6 w-6 p-0 shrink-0'
            >
              <Copy className='w-3 h-3' />
            </Button>
          </div>

          {/* Status and Currency - Flex Between */}
          <div className='flex items-center justify-between mb-3'>
            <div className='flex items-center gap-2'>
              {status === ENTITY_STATUS.active ? (
                <CheckCircle className='w-4 h-4 text-green-500' />
              ) : (
                <AlertCircle className='w-4 h-4 text-orange-500' />
              )}
              <Badge
                variant={
                  status === ENTITY_STATUS.active
                    ? 'default'
                    : status === ENTITY_STATUS.draft
                      ? 'secondary'
                      : 'destructive'
                }
              >
                {status}
              </Badge>
            </div>
            <Badge variant='outline' className='text-xs'>
              {currency.toUpperCase()}
            </Badge>
          </div>

          {/* Billing Period and Target Audience */}
          <div className='flex items-center gap-2 flex-wrap'>
            {billing_period && (
              <Badge variant='outline' className='text-xs'>
                {getBillingPeriodLabel(billing_period)}
              </Badge>
            )}
            {target_audience && (
              <Badge
                variant='outline'
                className={`text-xs ${getTargetAudienceColor(target_audience)}`}
              >
                {getTargetAudienceLabel(target_audience)}
              </Badge>
            )}
          </div>
        </div>

        {/* Divider after plan name */}
        <div className='border-t border-border mb-6'></div>

        {/* Side Navigation */}
        <div className='space-y-2 mb-6'>
          {/* biome-ignore lint/a11y/useSemanticElements: styled nav item */}
          <div
            onClick={() => onActiveMenuChange('pricing')}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ')
                onActiveMenuChange('pricing')
            }}
            role='button'
            tabIndex={0}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
              activeMenu === 'pricing'
                ? 'bg-slate-100/50 dark:bg-slate-800/50 font-medium'
                : 'hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
            }`}
          >
            <span>Pricing</span>
            <ChevronRight className='w-4 h-4 text-muted-foreground' />
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: styled nav item */}
          <div
            onClick={() => onActiveMenuChange('entitlements')}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ')
                onActiveMenuChange('entitlements')
            }}
            role='button'
            tabIndex={0}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
              activeMenu === 'entitlements'
                ? 'bg-slate-100/50 dark:bg-slate-800/50 font-medium'
                : 'hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
            }`}
          >
            <span>Entitlements</span>
            <ChevronRight className='w-4 h-4 text-muted-foreground' />
          </div>
        </div>

        {/* Divider */}
        <div className='border-t border-border mb-6'></div>

        {/* Free Trial Section - Read Only Display */}
        <div className='mb-6'>
          <h4 className='text-sm font-medium mb-3'>Free trial</h4>
          {isTrialLoading ? (
            <Loader2 className='w-4 h-4 animate-spin text-muted-foreground' />
          ) : trialSettings?.trial_available ? (
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <Badge
                  variant='default'
                  className='bg-green-500 hover:bg-green-500'
                >
                  Free Trial Enabled
                </Badge>
              </div>
              <div className='flex items-center gap-2'>
                <Timer className='w-3 h-3 text-muted-foreground' />
                <Badge variant='outline'>
                  {trialSettings.trial_days}{' '}
                  {trialSettings.trial_days === 1 ? 'day' : 'days'}
                </Badge>
              </div>
              <p className='text-xs text-muted-foreground'>
                Subscriptions to this addon will start with a{' '}
                {trialSettings.trial_days}-day free trial
              </p>
            </div>
          ) : (
            <Badge variant='secondary'>No Free Trial</Badge>
          )}
        </div>

        {/* Divider after free trial */}
        <div className='border-t border-border mb-6'></div>

        <div className='flex gap-2 mt-auto'>
          <Button
            variant='outline'
            size='sm'
            onClick={onEdit}
            className='flex-1 h-8 text-xs'
          >
            <Edit className='w-3 h-3 mr-1' />
            Edit
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={onToggleStatus}
            className='flex-1 h-8 text-xs'
          >
            {status === ENTITY_STATUS.draft ? (
              <>
                <Play className='w-3 h-3 mr-1' />
                Activate
              </>
            ) : status === ENTITY_STATUS.active ? (
              <>
                <Archive className='w-3 h-3 mr-1' />
                Archive
              </>
            ) : (
              <>
                <Play className='w-3 h-3 mr-1' />
                Reactivate
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
